import React, { useState, useEffect, useRef } from "react";
import useNav from "./hooks/useNav";
import { UserCircle2, Phone, ClipboardList, LogOut, Send, Info } from "lucide-react";
import Header from "./components/Header";
import BottomNav from "./components/BottomNav";
import { useUser } from "./context/UserContext";
import appConfig from "./config";
import { toast } from "react-toastify";

const GUEST_NAME_KEY  = "guest_name";
const GUEST_PHONE_KEY = "guest_phone";
const POLL_INTERVAL   = 2000;
const POLL_TIMEOUT    = 180000; // 3 минуты

const ProfilePage = () => {
    const { user, isAuthenticated, logout, login } = useUser();
    const navigate = useNav();

    const [tgLoading, setTgLoading]     = useState(false);
    const [polling, setPolling]         = useState(false);
    const pollTimer  = useRef(null);
    const pollExpiry = useRef(null);

    const guestName  = localStorage.getItem(GUEST_NAME_KEY)  || "";
    const guestPhone = localStorage.getItem(GUEST_PHONE_KEY) || "";

    useEffect(() => () => clearInterval(pollTimer.current), []);

    const startPolling = (sessionId) => {
        setPolling(true);
        pollExpiry.current = Date.now() + POLL_TIMEOUT;

        pollTimer.current = setInterval(async () => {
            if (Date.now() > pollExpiry.current) {
                stopPolling();
                toast.error("Время ожидания авторизации истекло");
                return;
            }

            try {
                const res  = await fetch(
                    `${appConfig.endpoint}/auth/telegram/check?session_id=${sessionId}`
                );
                const data = await res.json();

                if (data.status === "ok") {
                    stopPolling();
                    login(data.user, data.token);
                    toast.success("Вы успешно вошли!");
                } else if (data.status === "expired") {
                    stopPolling();
                    toast.error("Ссылка для входа устарела");
                }
            } catch {
                // сеть недоступна — продолжаем пробовать
            }
        }, POLL_INTERVAL);
    };

    const stopPolling = () => {
        clearInterval(pollTimer.current);
        setPolling(false);
        setTgLoading(false);
    };

    const handleTelegramLogin = async () => {
        setTgLoading(true);
        try {
            const headers = { "Content-Type": "application/json" };
            if (appConfig.token) {
                headers["Authorization"] = `Bearer ${appConfig.token}`;
            }
            const res = await fetch(`${appConfig.endpoint}/auth/telegram/init`, {
                method: "POST",
                headers,
            });
            const data = await res.json();

            if (!data.success) {
                toast.error("Не удалось начать авторизацию");
                setTgLoading(false);
                return;
            }

            window.open(data.deep_link, "_blank");
            startPolling(data.session_id);
        } catch {
            toast.error("Ошибка соединения");
            setTgLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-gray-100 pb-32">
            <Header />

            <div className="max-w-screen-md mx-auto px-4 pt-6 space-y-4">

                {/* Аватар + имя */}
                <div className="bg-white rounded-2xl p-6 flex flex-col items-center text-center shadow-sm">
                    <div className="w-20 h-20 rounded-full bg-brandBlue/15 flex items-center justify-center mb-3">
                        <UserCircle2 size={48} className="text-brandBlue" strokeWidth={1.5} />
                    </div>

                    {isAuthenticated && user ? (
                        <>
                            <p className="text-lg font-semibold text-gray-900">
                                {user.first_name || user.username || "Пользователь"}
                                {user.last_name ? " " + user.last_name : ""}
                            </p>
                            {user.phone && (
                                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                                    <Phone size={13} /> {user.phone}
                                </p>
                            )}
                            {user.telegram_id ? (
                                <span className="mt-2 text-xs bg-brandBlue/10 text-brandBlue px-2 py-0.5 rounded-full">
                                    Telegram привязан
                                </span>
                            ) : (
                                <span className="mt-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                                    Гостевой режим
                                </span>
                            )}
                        </>
                    ) : (
                        <>
                            <p className="text-base font-medium text-gray-700">
                                {guestName || "Гость"}
                            </p>
                            {guestPhone && (
                                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                                    <Phone size={13} /> {guestPhone}
                                </p>
                            )}
                            <span className="mt-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                                Гостевой режим
                            </span>
                        </>
                    )}
                </div>

                {/* Привязка Telegram — для гостей и для авторизованных без telegram_id */}
                {(!isAuthenticated || (isAuthenticated && !user?.telegram_id)) && (
                    <div className="bg-white rounded-2xl shadow-sm p-4">
                        {polling ? (
                            <div className="flex flex-col items-center py-2 gap-2">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brandBlue" />
                                <p className="text-sm text-gray-500">Ожидаем подтверждения в Telegram...</p>
                                <button
                                    onClick={stopPolling}
                                    className="text-xs text-gray-400 underline mt-1"
                                >
                                    Отмена
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={handleTelegramLogin}
                                disabled={tgLoading}
                                className="w-full flex items-center justify-center gap-2 bg-brandBlue hover:bg-brandBlue/90 active:bg-brandBlue/80 text-white font-medium py-3 px-4 rounded-xl transition disabled:opacity-60"
                            >
                                <Send size={18} />
                                {tgLoading ? "Подключение..." : isAuthenticated ? "Привязать Telegram" : "Войти через Telegram"}
                            </button>
                        )}
                    </div>
                )}

                {/* Действия */}
                <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100">
                    <button
                        onClick={() => navigate("/about")}
                        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition text-left"
                    >
                        <Info size={20} className="text-gray-400 shrink-0" />
                        <span className="text-gray-800 font-medium">О ресторане</span>
                    </button>
                    <button
                        onClick={() => navigate("/history")}
                        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition text-left"
                    >
                        <ClipboardList size={20} className="text-gray-400 shrink-0" />
                        <span className="text-gray-800 font-medium">История заказов</span>
                    </button>

                    {isAuthenticated && (
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-5 py-4 hover:bg-red-50 transition text-left"
                        >
                            <LogOut size={20} className="text-red-400 shrink-0" />
                            <span className="text-red-500 font-medium">Выйти из аккаунта</span>
                        </button>
                    )}
                </div>
            </div>

            <BottomNav />
        </div>
    );
};

export default ProfilePage;
